import React, { forwardRef, useMemo, useState, useCallback } from 'react';
import {
  Table,
  TableProps,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  Typography,
  IconButton,
  Checkbox,
  Chip,
  Tooltip,
  Fade,
  Skeleton,
  TablePagination,
  TableSortLabel,
  Collapse,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS, SAP_TRANSITIONS } from '../../theme/sapDesignSystem';

// Column interface
export interface SAPTableColumn<T = any> {
  id: keyof T;
  label: string;
  type?: 'text' | 'number' | 'date' | 'boolean' | 'status' | 'action' | 'expand' | 'select';
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  renderHeader?: (column: SAPTableColumn<T>) => React.ReactNode;
  getValue?: (row: T) => any;
  formatter?: (value: any) => string;
  statusConfig?: {
    mapping: Record<string, { label: string; color: string; backgroundColor: string }>;
    defaultColor?: string;
  };
  actionConfig?: {
    icon?: React.ReactNode;
    tooltip?: string;
    onClick?: (row: T, event: React.MouseEvent) => void;
    disabled?: (row: T) => boolean;
  };
}

// Sort configuration
export interface SAPTableSort {
  field: string;
  direction: 'asc' | 'desc';
}

// Component variants using CVA
const tableVariants = cva(
  'transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        outlined: 'border border-gray-200 rounded-lg',
        elevated: 'shadow-lg rounded-lg',
        simple: 'border-none',
      },
      size: {
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg',
      },
      density: {
        compact: 'p-2',
        normal: 'p-4',
        comfortable: 'p-6',
      },
    },
    defaultVariants: {
      variant: 'outlined',
      size: 'md',
      density: 'normal',
    },
  }
);

// Enhanced TypeScript interface
export interface SAPTableProps<T = any> extends Omit<TableProps, 'size'> {
  // Basic props
  variant?: 'outlined' | 'elevated' | 'simple';
  size?: 'sm' | 'md' | 'lg';
  density?: 'compact' | 'normal' | 'comfortable';
  
  // Data
  data: T[];
  columns: SAPTableColumn<T>[];
  
  // Selection
  selectable?: boolean;
  selectedRows?: T[];
  onSelectionChange?: (selectedRows: T[]) => void;
  
  // Sorting
  sortable?: boolean;
  sort?: SAPTableSort;
  onSortChange?: (sort: SAPTableSort) => void;
  
  // Pagination
  pagination?: boolean;
  page?: number;
  rowsPerPage?: number;
  totalRows?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  
  // Loading
  loading?: boolean;
  loadingRows?: number;
  
  // Empty state
  emptyText?: string;
  emptyIcon?: React.ReactNode;
  
  // Expandable rows
  expandable?: boolean;
  expandedRows?: string[];
  onExpandedChange?: (expandedRows: string[]) => void;
  renderExpandedContent?: (row: T, index: number) => React.ReactNode;
  
  // Actions
  onRowClick?: (row: T, event: React.MouseEvent) => void;
  onRowDoubleClick?: (row: T, event: React.MouseEvent) => void;
  
  // Styling
  customStyles?: React.CSSProperties;
  rowStyles?: (row: T, index: number) => React.CSSProperties;
  
  // Callbacks
  onRowSelect?: (row: T, selected: boolean) => void;
  onColumnSort?: (column: SAPTableColumn<T>, direction: 'asc' | 'desc') => void;
  
  // Accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
}

// Styled components
type TableContainerStyleProps = {
  variant?: 'outlined' | 'elevated' | 'simple';
  size?: 'sm' | 'md' | 'lg';
  density?: 'compact' | 'normal' | 'comfortable';
  customStyles?: React.CSSProperties;
};

const StyledTableContainer = styled(TableContainer, {
  shouldForwardProp: (prop) => 
    !['variant', 'size', 'density', 'customStyles'].includes(prop as string),
})<TableContainerStyleProps>(({ theme, variant = 'outlined', size = 'md', density = 'normal', customStyles }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: variant === 'simple' ? 0 : SAP_SPACING.component.borderRadius,
  border: variant === 'simple' ? 'none' : `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: variant === 'elevated' ? SAP_SHADOWS.lg : 'none',
  overflow: 'hidden',
  
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  
  ...customStyles,
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    backgroundColor: SAP_COLORS.sapLightBlue,
    
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: SAP_COLORS.hover,
      },
    },
  },
  
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      transition: SAP_TRANSITIONS.fast,
      borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
      
      '&:hover': {
        backgroundColor: SAP_COLORS.hover,
      },
      
      '&:last-child': {
        borderBottom: 'none',
      },
      
      '&.Mui-selected': {
        backgroundColor: SAP_COLORS.sapLightBlue,
        
        '&:hover': {
          backgroundColor: SAP_COLORS.sapLightBlue,
        },
      },
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
  padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
  fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
  fontSize: SAP_TYPOGRAPHY.fontSize.sm,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
  color: SAP_COLORS.sapGray7,
  lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
  
  '&.MuiTableCell-head': {
    backgroundColor: SAP_COLORS.sapLightBlue,
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    borderBottom: `2px solid ${SAP_COLORS.sapGray3}`,
  },
  
  '&.MuiTableCell-body': {
    color: SAP_COLORS.sapGray7,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: SAP_TRANSITIONS.fast,
  cursor: 'pointer',
  
  '&:hover': {
    backgroundColor: SAP_COLORS.hover,
  },
  
  '&.Mui-selected': {
    backgroundColor: SAP_COLORS.sapLightBlue,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.sapLightBlue,
    },
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontSize: SAP_TYPOGRAPHY.fontSize.xs,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  height: '20px',
  borderRadius: SAP_SPACING.component.borderRadius,
}));

// Main component
export const SAPTable = forwardRef<HTMLDivElement, SAPTableProps>(
  (
    {
      variant = 'outlined',
      size = 'md',
      density = 'normal',
      data = [],
      columns = [],
      selectable = false,
      selectedRows = [],
      onSelectionChange,
      sortable = false,
      sort,
      onSortChange,
      pagination = false,
      page = 0,
      rowsPerPage = 10,
      totalRows = 0,
      onPageChange,
      onRowsPerPageChange,
      loading = false,
      loadingRows = 5,
      emptyText = 'No data available',
      emptyIcon,
      expandable = false,
      expandedRows = [],
      onExpandedChange,
      renderExpandedContent,
      onRowClick,
      onRowDoubleClick,
      customStyles,
      rowStyles,
      onRowSelect,
      onColumnSort,
      ariaLabel,
      ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    // Local state
    const [localSort, setLocalSort] = useState<SAPTableSort | null>(sort || null);
    const [localExpandedRows, setLocalExpandedRows] = useState<string[]>(expandedRows || []);

    // Memoized sorted data
    const sortedData = useMemo(() => {
      if (!localSort || !sortable) return data;
      
      return [...data].sort((a, b) => {
        const column = columns.find(col => col.id === localSort.field);
        if (!column) return 0;
        
        const getValue = column.getValue || ((row: any) => row[column.id]);
        const aValue = getValue(a);
        const bValue = getValue(b);
        
        if (aValue === bValue) return 0;
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        const comparison = aValue < bValue ? -1 : 1;
        return localSort.direction === 'asc' ? comparison : -comparison;
      });
    }, [data, localSort, sortable, columns]);

    // Handle sort change
    const handleSortChange = useCallback((field: string) => {
      const newSort: SAPTableSort = {
        field,
        direction: localSort?.field === field && localSort.direction === 'asc' ? 'desc' : 'asc',
      };
      
      setLocalSort(newSort);
      onSortChange?.(newSort);
    }, [localSort, onSortChange]);

    // Handle row selection
    const handleRowSelect = useCallback((row: any, selected: boolean) => {
      if (selected) {
        const newSelected = [...selectedRows, row];
        onSelectionChange?.(newSelected);
        onRowSelect?.(row, true);
      } else {
        const newSelected = selectedRows.filter(r => r !== row);
        onSelectionChange?.(newSelected);
        onRowSelect?.(row, false);
      }
    }, [selectedRows, onSelectionChange, onRowSelect]);

    // Handle select all
    const handleSelectAll = useCallback((selected: boolean) => {
      if (selected) {
        onSelectionChange?.(sortedData);
      } else {
        onSelectionChange?.([]);
      }
    }, [sortedData, onSelectionChange]);

    // Handle row expand
    const handleRowExpand = useCallback((rowId: string) => {
      const newExpanded = localExpandedRows.includes(rowId)
        ? localExpandedRows.filter(id => id !== rowId)
        : [...localExpandedRows, rowId];
      
      setLocalExpandedRows(newExpanded);
      onExpandedChange?.(newExpanded);
    }, [localExpandedRows, onExpandedChange]);

    // Render cell content
    const renderCell = useCallback((column: SAPTableColumn, row: any, index: number) => {
      const value = column.getValue ? column.getValue(row) : row[column.id];
      
      if (column.render) {
        return column.render(value, row, index);
      }

      switch (column.type) {
        case 'status':
          if (!column.statusConfig) return value;
          const status = column.statusConfig.mapping[value] || {
            label: value,
            color: column.statusConfig.defaultColor || SAP_COLORS.sapGray5,
            backgroundColor: SAP_COLORS.sapGray2,
          };
          return (
            <StatusChip
              label={status.label}
              sx={{
                backgroundColor: status.backgroundColor,
                color: status.color,
              }}
            />
          );

        case 'boolean':
          return value ? 'Yes' : 'No';

        case 'date':
          return value ? new Date(value).toLocaleDateString() : '';

        case 'number':
          return column.formatter ? column.formatter(value) : value?.toLocaleString();

        case 'action':
          if (!column.actionConfig) return null;
          const { icon, tooltip, onClick, disabled } = column.actionConfig;
          const isDisabled = disabled ? disabled(row) : false;
          return (
            <Tooltip title={tooltip || ''}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick?.(row, e);
                }}
                disabled={isDisabled}
                sx={{ color: SAP_COLORS.sapGray5 }}
              >
                {icon}
              </IconButton>
            </Tooltip>
          );

        case 'expand':
          const isExpanded = localExpandedRows.includes(row.id || index.toString());
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleRowExpand(row.id || index.toString());
              }}
              sx={{ color: SAP_COLORS.sapGray5 }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          );

        case 'select':
          const isSelected = selectedRows.includes(row);
          return (
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                handleRowSelect(row, e.target.checked);
              }}
              sx={{ color: SAP_COLORS.sapBlue }}
            />
          );

        default:
          return column.formatter ? column.formatter(value) : value;
      }
    }, [localExpandedRows, selectedRows, handleRowExpand, handleRowSelect]);

    // Render header cell
    const renderHeaderCell = useCallback((column: SAPTableColumn) => {
      if (column.renderHeader) {
        return column.renderHeader(column);
      }

      const isSorted = localSort?.field === column.id;
      const sortDirection = isSorted ? localSort.direction : undefined;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              color: SAP_COLORS.sapGray7,
            }}
          >
            {column.label}
          </Typography>
          
          {column.sortable && sortable && (
            <TableSortLabel
              active={isSorted}
              direction={sortDirection}
              onClick={() => handleSortChange(column.id as string)}
              sx={{
                color: SAP_COLORS.sapGray5,
                '&.Mui-active': {
                  color: SAP_COLORS.sapBlue,
                },
              }}
            />
          )}
        </Box>
      );
    }, [localSort, sortable, handleSortChange]);

    // Enhanced accessibility props
    const accessibilityProps = {
      'aria-label': ariaLabel || 'Data table',
      'aria-describedby': ariaDescribedBy,
    };

    return (
      <Box ref={ref} sx={{ width: '100%' }}>
        {/* Loading indicator */}
        {loading && (
          <LinearProgress
            sx={{
              backgroundColor: SAP_COLORS.sapGray2,
              '& .MuiLinearProgress-bar': {
                backgroundColor: SAP_COLORS.sapBlue,
              },
            }}
          />
        )}

        <StyledTableContainer
          variant={variant}
          size={size}
          density={density}
          customStyles={customStyles}
          {...accessibilityProps}
        >
          <StyledTable {...props}>
            <TableHead>
              <TableRow>
                {selectable && (
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedRows.length > 0 && selectedRows.length < sortedData.length}
                      checked={selectedRows.length > 0 && selectedRows.length === sortedData.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      sx={{ color: SAP_COLORS.sapBlue }}
                    />
                  </StyledTableCell>
                )}
                
                {expandable && (
                  <StyledTableCell padding="checkbox" />
                )}
                
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id as string}
                    align={column.align || 'left'}
                    style={{
                      width: column.width,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                    }}
                  >
                    {renderHeaderCell(column)}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            
            <TableBody>
              {loading ? (
                // Loading skeleton rows
                Array.from({ length: loadingRows }).map((_, index) => (
                  <StyledTableRow key={`loading-${index}`}>
                    {selectable && (
                      <StyledTableCell padding="checkbox">
                        <Skeleton variant="rectangular" width={20} height={20} />
                      </StyledTableCell>
                    )}
                    
                    {expandable && (
                      <StyledTableCell padding="checkbox">
                        <Skeleton variant="rectangular" width={20} height={20} />
                      </StyledTableCell>
                    )}
                    
                    {columns.map((column) => (
                      <StyledTableCell key={column.id as string}>
                        <Skeleton variant="text" width="80%" />
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))
              ) : sortedData.length === 0 ? (
                // Empty state
                <TableRow>
                  <StyledTableCell
                    colSpan={columns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0)}
                    align="center"
                    sx={{ padding: SAP_SPACING.xl }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      {emptyIcon}
                      <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5 }}>
                        {emptyText}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                </TableRow>
              ) : (
                // Data rows
                sortedData.map((row, index) => {
                  const isSelected = selectedRows.includes(row);
                  const isExpanded = localExpandedRows.includes(row.id || index.toString());
                  
                  return (
                    <React.Fragment key={row.id || index}>
                      <StyledTableRow
                        selected={isSelected}
                        onClick={(e) => onRowClick?.(row, e)}
                        onDoubleClick={(e) => onRowDoubleClick?.(row, e)}
                        sx={rowStyles ? rowStyles(row, index) : {}}
                      >
                        {selectable && (
                          <StyledTableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleRowSelect(row, e.target.checked);
                              }}
                              sx={{ color: SAP_COLORS.sapBlue }}
                            />
                          </StyledTableCell>
                        )}
                        
                        {expandable && (
                          <StyledTableCell padding="checkbox">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowExpand(row.id || index.toString());
                              }}
                              sx={{ color: SAP_COLORS.sapGray5 }}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </StyledTableCell>
                        )}
                        
                        {columns.map((column) => (
                          <StyledTableCell
                            key={column.id as string}
                            align={column.align || 'left'}
                            style={{
                              width: column.width,
                              minWidth: column.minWidth,
                              maxWidth: column.maxWidth,
                            }}
                          >
                            {renderCell(column, row, index)}
                          </StyledTableCell>
                        ))}
                      </StyledTableRow>
                      
                      {/* Expanded content */}
                      {expandable && isExpanded && renderExpandedContent && (
                        <TableRow>
                          <StyledTableCell
                            colSpan={columns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0)}
                            sx={{
                              padding: 0,
                              borderBottom: 'none',
                              backgroundColor: SAP_COLORS.sapGray1,
                            }}
                          >
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ padding: SAP_SPACING.md }}>
                                {renderExpandedContent(row, index)}
                              </Box>
                            </Collapse>
                          </StyledTableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        {/* Pagination */}
        {pagination && (
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(_, newPage) => onPageChange?.(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onRowsPerPageChange?.(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              backgroundColor: SAP_COLORS.sapWhite,
              borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: SAP_COLORS.sapGray6,
                fontSize: SAP_TYPOGRAPHY.fontSize.sm,
              },
            }}
          />
        )}
      </Box>
    );
  }
);

// Display name for debugging
SAPTable.displayName = 'SAPTable';

// Export types for external use
export { tableVariants };
